import React, { useEffect, useMemo, useRef, useState } from 'react';
import { OwnerOption } from './VehicleEditor';

/**
 * Choix du propriétaire d'une annonce, par recherche.
 *
 * C'était une liste déroulante de TOUS les comptes de la plateforme, dans
 * l'ordre alphabétique : praticable sur une base de démonstration, ingérable
 * dès que les comptes se comptent en centaines — il fallait faire défiler à
 * l'aveugle pour trouver quelqu'un dont on connaît le nom.
 *
 * Trois résultats au maximum, volontairement. Un transfert d'annonce n'est pas
 * une exploration : on sait à qui on la donne, on tape son nom. Une liste plus
 * longue inviterait à choisir « celui qui ressemble », alors que la bonne
 * réaction devant trop de résultats est d'affiner la recherche — ce que le
 * compteur de résultats restants rappelle.
 */

const MAX_SUGGESTIONS = 3;

/** Repli sans accents ni casse : « Rami » doit trouver « Râmi », et l'inverse. */
const fold = (value: string) =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export type OwnerPickerProps = {
  owners: OwnerOption[];
  /**
   * Où en est le chargement de `owners`.
   *
   * Sans cette information, le champ ne peut que mentir : une liste vide parce
   * que la requête a échoué produit exactement le même « aucun compte ne
   * correspond » qu'une recherche sans résultat, et l'admin en conclut que la
   * personne n'a pas de compte.
   */
  status: 'loading' | 'ready' | 'error';
  error: string;
  onRetry: () => void;
  /** `''` = la plateforme (owner_id NULL en base). */
  value: string;
  onChange: (ownerId: string) => void;
};

const OwnerPicker: React.FC<OwnerPickerProps> = ({ owners, status, error, onRetry, value, onChange }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => owners.find((o) => o.id === value) || null, [owners, value]);
  // Trois états, et non deux : la plateforme (value vide), un compte connu, et
  // un compte que la liste ne contient pas. Le troisième s'affichait comme le
  // premier — une annonce de particulier apparaissait donc comme appartenant à
  // « Plateforme Dingo », ce qui est faux et invite à enregistrer une annonce
  // en croyant confirmer un propriétaire qu'on n'a jamais vu.
  const unresolved = value !== '' && !selected;

  const matches = useMemo(() => {
    const needle = fold(query.trim());
    if (!needle) return owners;
    return owners.filter((owner) => fold(`${owner.name} ${owner.email || ''}`).includes(needle));
  }, [owners, query]);

  const suggestions = matches.slice(0, MAX_SUGGESTIONS);
  const hidden = matches.length - suggestions.length;

  // « Plateforme » ferme la liste des comptes : c'est une destination, pas une
  // personne, et elle reste donc atteignable quelle que soit la recherche.
  // Recalculé à chaque rendu plutôt que mémorisé : quatre entrées au plus, et
  // une clé de mémorisation bricolée coûterait plus cher que le calcul.
  const rows = [...suggestions.map((o) => o.id), ''];

  useEffect(() => setHighlight(0), [query]);

  // La liste s'ouvre vers le bas, dans un panneau qui défile : si le champ est
  // près du bas de l'onglet, elle naît hors du cadre visible. L'amener à l'écran
  // évite d'avoir à deviner qu'il faut faire défiler pour voir des résultats
  // qu'on vient soi-même de demander.
  useEffect(() => {
    if (open && listRef.current) {
      // `nearest` : le défilement minimal qui ramène la liste dans le cadre.
      // `end` alignait son bord bas sur celui du panneau et poussait le champ
      // de saisie hors de vue — on tapait alors sans voir ce qu'on tapait.
      listRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [open, suggestions.length]);

  // Un clic hors du champ referme la liste. Sans ça, elle recouvrait les
  // champs suivants de l'onglet jusqu'au prochain choix.
  useEffect(() => {
    if (!open) return;
    const handle = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const choose = (ownerId: string) => {
    onChange(ownerId);
    setQuery('');
    setOpen(false);
  };

  const handleKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      // La modale se ferme aussi sur Échap : sans cet arrêt, chercher un compte
      // puis se raviser fermait tout l'éditeur et perdait la saisie en cours.
      if (open) {
        event.stopPropagation();
        setOpen(false);
      }
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setHighlight((prev) => {
        const next = event.key === 'ArrowDown' ? prev + 1 : prev - 1;
        return (next + rows.length) % rows.length;
      });
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (open && rows[highlight] !== undefined) choose(rows[highlight]);
    }
  };

  const rowClass = (index: number) =>
    `w-full text-left px-3 py-2 text-sm transition-colors ${
      highlight === index ? 'bg-indigo-50 text-indigo-800' : 'text-gray-700 hover:bg-gray-50'
    }`;

  return (
    <div ref={containerRef}>
      {/* Sélection courante — toujours visible, y compris liste ouverte : c'est
          la moitié de l'information dans un transfert. */}
      <div className={`flex items-center gap-2.5 px-3 py-2.5 border rounded-lg mb-2 ${
        unresolved ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'
      }`}>
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
          unresolved ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'
        }`}>
          {unresolved ? '?' : selected ? selected.name.slice(0, 1).toUpperCase() : '⬢'}
        </span>
        <span className="min-w-0">
          <span className={`block text-sm truncate ${unresolved ? 'text-amber-900' : 'text-gray-800'}`}>
            {unresolved ? `Compte ${value.slice(0, 8)}…` : selected ? selected.name : 'Plateforme Dingo'}
          </span>
          <span className={`block text-[11px] truncate ${unresolved ? 'text-amber-700' : 'text-gray-400'}`}>
            {unresolved
              ? 'Propriétaire non chargé — ce n\'est pas la plateforme'
              : selected ? (selected.email || 'Sans email') : 'Aucun propriétaire particulier'}
          </span>
        </span>
      </div>

      {status === 'error' && (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-xs text-red-800">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-1.5 text-xs font-medium text-red-700 underline hover:text-red-900"
          >
            Réessayer de charger les comptes
          </button>
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          disabled={status !== 'ready'}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => status === 'ready' && setOpen(true)}
          // `onClick` en plus de `onFocus` : après un choix, la liste se ferme
          // mais le champ GARDE le focus. Un second clic dessus ne déclenchait
          // donc aucun `focus`, et la liste ne se rouvrait plus — il fallait
          // cliquer ailleurs puis revenir pour corriger sa sélection.
          onClick={() => status === 'ready' && setOpen(true)}
          onKeyDown={handleKey}
          placeholder={
            status === 'loading'
              ? 'Chargement des comptes…'
              : status === 'error'
                ? 'Comptes indisponibles'
                : 'Rechercher un compte par nom ou email…'
          }
          className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
        />
        <svg className="w-4 h-4 text-gray-300 absolute left-3 top-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {/* Dans le flux, et non en surimpression. L'éditeur fait défiler le
          contenu de son onglet (`overflow-y-auto`) : une liste positionnée en
          absolu y était rognée par le bord bas du panneau — sur une fenêtre
          courte, on ne voyait plus que deux résultats sur quatre, et
          « Plateforme » devenait inatteignable. En flux, le panneau se
          contente de défiler. Le prix est le décalage des champs suivants
          pendant la recherche ; il vaut mieux qu'une liste tronquée. */}
      {open && (
        <div ref={listRef} className="mt-1 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          {/* « Aucun compte ne correspond » n'est dit que si la liste est
              réellement chargée ET non vide. Sinon c'est un constat sur notre
              propre échec présenté comme un fait sur l'utilisateur cherché. */}
          {suggestions.length === 0 && query.trim() !== '' && owners.length > 0 && (
            <p className="px-3 py-2.5 text-xs text-gray-400">Aucun compte ne correspond à « {query.trim()} ».</p>
          )}
          {owners.length === 0 && status === 'ready' && (
            <p className="px-3 py-2.5 text-xs text-gray-400">Aucun compte n'a été chargé.</p>
          )}

          {suggestions.map((owner, index) => (
            <button key={owner.id} type="button" onClick={() => choose(owner.id)} className={rowClass(index)}>
              <span className="block truncate">{owner.name}</span>
              <span className="block text-[11px] text-gray-400 truncate">{owner.email || 'Sans email'}</span>
            </button>
          ))}

          {hidden > 0 && (
            <p className="px-3 py-1.5 text-[11px] text-gray-400 border-t border-gray-100">
              {hidden} autre{hidden > 1 ? 's' : ''} compte{hidden > 1 ? 's' : ''} correspond{hidden > 1 ? 'ent' : ''} — précisez la recherche.
            </p>
          )}

          <button
            type="button"
            onClick={() => choose('')}
            className={`${rowClass(suggestions.length)} border-t border-gray-100`}
          >
            <span className="block">Plateforme Dingo</span>
            <span className="block text-[11px] text-gray-400">Retirer l'annonce à son propriétaire</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default OwnerPicker;
